import * as Api from '@ViewModels';
import { ResourceAutoCompleteViewModelType } from '@ViewModels';
import {
	ISimpleAutoCompleteSearchFieldItemSelectionEvent,
	SimpleAutoCompleteSearchField,
} from '@WebComponents/autocomplete/SimpleAutoCompleteSearchField';
import { LoadingSpinner } from '@WebComponents/LoadingSpinner';
import { Modal } from '@WebComponents/Modal';
import { css } from 'aphrodite';
import * as React from 'react';
import { toast } from 'sonner';
import { useErrorMessages } from '../../../../models/hooks/appStateHooks';
import { useEventLogging } from '../../../../models/Logging';
import { useContactAddConnectionMutation } from '../../../../queries';
import { useSystemJob } from '../../../containers/PeopleV2/PeopleContainer/PeopleActionsBar/useActionBarStatus';
import { Button } from '../../Button';
import { styleSheet } from './styles';

interface IProps {
	isOpen: boolean;
	onSave: (newConnectionId: string) => void;
	onCancel: () => void;
	selectedContactIds?: string[];
	excludedContactIds?: string[];
	filterCriteria?: Api.IContactsFilterRequest;
	ownershipFilter?: Api.IOwnershipFilter;
	includeFilterInRequest?: boolean;
}

export const AddContactConnection: React.FC<IProps> = ({
	isOpen,
	onSave,
	onCancel,
	selectedContactIds,
	excludedContactIds,
	filterCriteria,
	ownershipFilter,
	includeFilterInRequest = false,
}) => {
	// State
	const [selectedUser, setSelectedUser] = React.useState<Api.IUser | null>(null);

	// Hooks
	const { logInput, logApiError } = useEventLogging();
	const errorMessages = useErrorMessages();

	// Job monitoring
	const { startWatchingJob, isJobRunning } = useSystemJob(() => {
		toast.success('Connection added successfully!');
		onSave(selectedUser?.id || '');
	});

	// React Query mutation
	const contactAddConnectionMutation = useContactAddConnectionMutation({
		onSuccess: systemJob => {
			startWatchingJob(systemJob);
		},
		onError: error => {
			logApiError('AddConnection-Error', error);
			errorMessages.pushApiError(error);
		},
	});

	const handleUserSelected = (event: ISimpleAutoCompleteSearchFieldItemSelectionEvent) => {
		setSelectedUser(event.selection as Api.IUser);
	};

	const handleAddConnection = () => {
		if (!selectedUser) return;

		// filter and includeContactIds are mutually exclusive:
		// - When using filter mode (includeFilterInRequest=true): send filter + excludeContactIds
		// - When selecting specific contacts: send includeContactIds only
		const request = {
			excludeContactIds: includeFilterInRequest ? excludedContactIds : undefined,
			filter: includeFilterInRequest ? filterCriteria : undefined,
			ownershipFilter: includeFilterInRequest ? ownershipFilter : undefined,
			includeContactIds: includeFilterInRequest ? undefined : selectedContactIds,
			newConnectionId: selectedUser.id,
		};

		logInput('Done', 'Click', {
			excludeContactIds: request.excludeContactIds?.length ?? 0,
			filter: request.filter,
			includeContactIds: request.includeContactIds?.length ?? 0,
			newConnectionId: selectedUser.id,
		});

		contactAddConnectionMutation.mutate(request);
	};

	const handleCancel = () => {
		onCancel();
	};

	return (
		<Modal isOpen={isOpen} onRequestClose={onCancel}>
			<h2 className={css(styleSheet.header)}>Add Connection</h2>

			{isJobRunning ? (
				<LoadingSpinner type='large' />
			) : (
				<div className={css(styleSheet.content)}>
					<p className={css(styleSheet.description)}>
						Adding as a connection will share this contact with the selected co-worker.
					</p>
					<SimpleAutoCompleteSearchField
						disabled={contactAddConnectionMutation.isPending || isJobRunning}
						onClear={() => setSelectedUser(null)}
						onItemSelected={handleUserSelected}
						type={ResourceAutoCompleteViewModelType.User}
						placeholder='Search co-workers or add yourself'
						pageSize={5}
						resultsLimit={5}
					/>

					<div className={css(styleSheet.footer)}>
						<Button
							kind='primary'
							onClick={handleAddConnection}
							disabled={!selectedUser || contactAddConnectionMutation.isPending}
						>
							Done
						</Button>
						<Button kind='reverse' onClick={handleCancel} disabled={contactAddConnectionMutation.isPending}>
							Cancel
						</Button>
					</div>
				</div>
			)}
		</Modal>
	);
};
